import {isNumber}  from "underscore";

const SHORT_MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * Contains date utility functions.
 *
 * @class DateUtil
 * @constructor
 */
export class DateUtil {

     /**
     * Returns the current timestamp in milliseconds since the epoch.
     *
     * @return {Number} The current timestamp in ms.
     */
    static now() {
        return Date.now();
    }
    
    /**
     * Returns the year of the current date according to local time.
     *
     * @return {Number} The current year.
     */
    static currentYear(){ 
        return new Date().getFullYear();
    }

    static _oneDigitPrepender(value: number) {
        return value < 10 ? `0${value}` : value;
    }

    /**
     * Formats a date/timestamp in "YYYY-MM-DD, HH:mm" format.
     *
     * @param  {Number|Date} date The date to format. It can be null, in which case an empty string will be returned.
     * @return {String} The formatted date string or an empty string if the passed date is null.
     */
    static formatYYYYMMDDHHmm(date: Date) {
        if (!date) {
            return "";
        }
        if (isNumber(date)) {
            date = new Date(date);
        }
        const year   = date.getFullYear();
        const month  = this._oneDigitPrepender(date.getMonth() + 1);
        const day    = this._oneDigitPrepender(date.getDate());
        const hour   = this._oneDigitPrepender(date.getHours());
        const minute = this._oneDigitPrepender(date.getMinutes());
        return `${year}-${month}-${day}, ${hour}:${minute}`; // will coerce numbers into strings!
    }
    /**
     * Calculate the difference between two dates in days
     *
     * @param  {Number|Date} date1 The date to be calculate1 (Number or date); cannot be null
     * @param  {Number|Date} date2 The date to be calculate2 (Number or date); cannot be null
     * @return {Number} The days difference NOT rounded. It maybe a decimal value e.g. 1.2548
     */
    static diffDays(date1: Date, date2: Date) {
        if (isNumber(date1)) {
            date1 = new Date(date1);
        }
        if (isNumber(date2)) {
            date2 = new Date(date2);
        }
        const timeDiff = Math.abs(date2.getTime() - date1.getTime());
        return timeDiff / (1000 * 3600 * 24);
    }

    /**
     * Compute relative time difference between two timestamps and return a nicely formatted string representation.
     *
     * @param {Number}  current   current timestamp in ms
     * @param {Number} [previous] previous timestamp in ms. Optional. Defaults to 0.
     * @returns {string} a nicely formatted string representation
     */
    static timeDifference(current: number, previous: number): string {
        const msPerMinute = 60 * 1000;
        const msPerHour   = msPerMinute * 60;
        const msPerDay    = msPerHour * 24;
        const msPerMonth  = msPerDay * 30;
        const msPerYear   = msPerDay * 365;

        if (!previous) {
            previous = 0;
        }

        const elapsed = current - previous;

        if (elapsed < msPerHour) {
            const min = Math.round(elapsed / msPerMinute);
            if (min <= 1) {
                return "a minute";
            }
            return `${min} minutes`;
        } else if (elapsed < msPerDay) {
            const hour = Math.round(elapsed / msPerHour);
            let hourString = hour.toString();
            hourString += hour > 1 ? " hours" : " hour";
            return hourString;
        } else if (elapsed < msPerMonth) {
            const day = Math.round(elapsed / msPerDay);
            let dayString = day.toString();
            dayString += day > 1 ? " days" : " day";
            return dayString;
        } else if (elapsed < msPerYear) {
            const month = Math.round(elapsed / msPerMonth);
            let monthString = month.toString();
            monthString += month > 1 ? " months" : " month";
            return monthString;
        }

        const year = Math.round(elapsed / msPerYear);
        let yearString = year.toString();
        yearString += year > 1 ? " years" : " year";
        return yearString;
    }

    /**
     * Formats the supplied date/timestamp as a named month, e.g. "Jun", a double digit date, e.g. "06", a
     * comma followed by a four digit year, e.g. "2014". For example "Jun 06, 2014".
     *
     * @param {Number|Date} date The date to format. It can be null, in which case an empty string will be returned.
     * @return {String} The formatted date string or an empty string if the passed date is null.
     */
    static formatMMDDYYY(date: Date) {
        if (!date) {
            return "";
        }
        if (isNumber(date)) {
            date = new Date(date);
        }
        const year  = date.getFullYear();
        const month = SHORT_MONTH_NAMES[date.getMonth()];
        const day   = this._oneDigitPrepender(date.getDate());
        return `${month} ${day}, ${year}`;
    }
}


// WEBPACK FOOTER //
// ./src/shared/util/DateUtil.ts